/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useEffect} from 'react';
import {TCmsPage, TCmsPageData} from "@/app/types/prefetchedData/cmsPageData";
import {useAppSelector} from "@/app/hooks/redux";
import useGetCmsPage from "@/app/connector/pageBuilder/getCmsPage";
import jsonToJsx from "@/app/utils/jsonToJsx";
import {FullPageLoader} from "@/app/components/common/FullPageLoader";
import {CmsPage} from "@/generated/graphql";
import storeConfigConstant from "@/app/config/store.config";

export const isCmsPageDataExist = (response:CmsPage) => {
    return !!(response && (response.identifier));
}

const useGetCmsPageData = (options: {
    prefetchedCmsPageData?: TCmsPageData,
    identifier?: string
}) => {
    const {getCmsPageTrigger, loading, cmsPageContent, cmsPageData, error} = useGetCmsPage(<FullPageLoader />);
    const { customerData } = useAppSelector(state => state.customerReducer);
    const isLogged = customerData.customer.isLogin;
    const identifier = options.identifier;
    let result:TCmsPage = {
        cmsPageContent: <FullPageLoader />,
        cmsPageData: cmsPageData,
        loading: loading,
        error: error,
        notFound: false,
    }

    useEffect(() => {
        if(identifier && (!options.prefetchedCmsPageData || (isLogged && !storeConfigConstant.enableCmsPageCachingForLoggedUser))) {
            getCmsPageTrigger(identifier);
        }
    },[getCmsPageTrigger, identifier, isLogged])

    if(options.prefetchedCmsPageData && (!isLogged || storeConfigConstant.enableCmsPageCachingForLoggedUser)) {
        if(isCmsPageDataExist(options.prefetchedCmsPageData.cmsPageData)) {
            // Case if we got prefetched data
            const currentStorePageData = options.prefetchedCmsPageData;
            result = {
                cmsPageContent: jsonToJsx(currentStorePageData.cmsPageData.content_json ?? currentStorePageData.cmsPageData.content ?? '{}'),
                cmsPageData: currentStorePageData.cmsPageData,
                loading: false,
                error: false,
                notFound: false,
            }
        } else {
            result.notFound = true;
        }
    } else if (!loading && !error && cmsPageData && cmsPageContent) {
        if(isCmsPageDataExist(cmsPageData)) {
            result = {
                cmsPageContent: cmsPageContent,
                cmsPageData: cmsPageData,
                loading: loading,
                error: error,
                notFound: false,
            }
        } else {
            result.notFound = true;
        }
    }

    return result;
};

export default useGetCmsPageData;